import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Accordion, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { fetchFilters, setActivePanel, createFilterType } from './store/assetLibraryFilter.actions';
import { AssetLibraryFilterState, Filter } from './store/assetLibraryFilter.types';
import { AssetLibraryDispatch } from './store/assetLibraryFilter.types';
import FilterTypeRow from './FilterTypeRow';
import FilterPanel from './FilterPanel';
import Loader from '../../../Components/Utility/Loader/Loader';
import CustomTooltip from '../../../Components/Tooltip/CustomTooltip';
import { useComponent } from '../../UserSetting/UsersLibrary/UserRights';
import './AssetLibrary.css';
import { BombiixModal } from '../../../Components/BombiixModal';
import { NewInput } from '../../../Components/PIM/common';
import { AddIcon } from '../../../bombiix-icon';

interface AssetLibraryRootState {
  AssetLibraryFilters: AssetLibraryFilterState;
  Users: { logInRights: unknown };
}

function AssetLibraryTab(): JSX.Element {
  const dispatch = useDispatch<AssetLibraryDispatch>();
  const { t } = useTranslation('systemMaster');
  const { CheckActionPermission } = useComponent();
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [newFilterTypeLabel, setNewFilterTypeLabel] = useState<string>('');
  const [createError, setCreateError] = useState<string | undefined>(undefined);
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const { filters, loading, activePanel } = useSelector((state: AssetLibraryRootState) => state.AssetLibraryFilters);
  const { logInRights } = useSelector((state: AssetLibraryRootState) => state.Users);

  type CheckPermFn = (rights: unknown, section: string, module: string, action: string) => boolean;
  const checkPerm = CheckActionPermission as unknown as CheckPermFn;
  const masterAddEditAccess: boolean = checkPerm(logInRights, 'masters', 'assetLibrary', 'addEdit');
  const masterDeleteAccess: boolean = checkPerm(logInRights, 'masters', 'assetLibrary', 'delete');

  useEffect(() => {
    dispatch(fetchFilters());
  }, [dispatch]);

  const handleSettingsClick = (filterTypeId: string): void => {
    dispatch(setActivePanel(activePanel === filterTypeId ? null : filterTypeId));
  };

  const handleClosePanel = (): void => {
    dispatch(setActivePanel(null));
  };

  const activeFilter: Filter | undefined = filters.find((f) => f._id === activePanel);
  const canAddFilterType: boolean = masterAddEditAccess && !loading && filters.length < MAX_FILTER_TYPES;

  const handleOpenCreateModal = (): void => {
    setNewFilterTypeLabel('');
    setCreateError(undefined);
    setShowCreateModal(true);
  };

  const getNewFilterTypeLabelError = (trimmedLabel: string): string | undefined => {
    if (!trimmedLabel) return t('ASSET_LIBRARY.FILTER_TYPE_LABEL_REQUIRED');
    if (trimmedLabel.length > MAX_FILTER_TYPE_LABEL_LENGTH) return t('ASSET_LIBRARY.LABEL_TOO_LONG');
    if (filters.some((f) => f.label.trim().toLowerCase() === trimmedLabel.toLowerCase())) return t('ASSET_LIBRARY.DUPLICATE_FILTER_TYPE');
    return undefined;
  };

  const handleCreateFilterType = async (): Promise<void> => {
    const trimmedLabel = newFilterTypeLabel.trim();
    const labelError = getNewFilterTypeLabelError(trimmedLabel);
    if (labelError) {
      setCreateError(labelError);
      return;
    }
    setIsCreating(true);
    const createdFilter = (await dispatch(createFilterType(trimmedLabel, t))) as Filter | null;
    setIsCreating(false);
    if (!createdFilter) return;
    setShowCreateModal(false);
    dispatch(setActivePanel(createdFilter._id));
  };

  return (
    <>
      <Accordion className="mb-4 asset-library-filters-accordion">
        <Card>
          <Card.Header>
            <div className="title-container">
              <p>{t('ASSET_LIBRARY.FILTERS_ACCORDION')}</p>
            </div>
            <div className="action-title-btns single-btn">
              {canAddFilterType && (
                <CustomTooltip tooltipText={t('ASSET_LIBRARY.ADD_FILTER_TYPE')}>
                  <button className="btn d-inline-flex p-0" type="button" onClick={handleOpenCreateModal} aria-label="Add filter type">
                    <AddIcon size={24} />
                  </button>
                </CustomTooltip>
              )}
              <CustomTooltip tooltipText={t(showFilters ? 'CUSTOM_ATTRIBUTES.CLOSE' : 'CUSTOM_ATTRIBUTES.OPEN')}>
                <button className="btn d-inline-flex p-0" type="button" onClick={() => setShowFilters(!showFilters)}>
                  {showFilters ? <i className="icon-new-down-arrow-icon icon-inverse f-16" /> : <i className="icon-new-down-arrow-icon  f-16" />}
                </button>
              </CustomTooltip>
            </div>
          </Card.Header>
          {showFilters && (
            <Card.Body>
              {loading ? (
                <Loader />
              ) : (
                <PerfectScrollbar>
                  <ul>
                    {filters.map((filter) => (
                      <FilterTypeRow
                        key={filter._id}
                        filter={filter}
                        onSettingsClick={handleSettingsClick}
                        masterAddEditAccess={masterAddEditAccess}
                        masterDeleteAccess={masterDeleteAccess}
                      />
                    ))}
                  </ul>
                </PerfectScrollbar>
              )}
            </Card.Body>
          )}
        </Card>
      </Accordion>

      {activePanel && activeFilter && (
        <FilterPanel filter={activeFilter} onClose={handleClosePanel} masterAddEditAccess={masterAddEditAccess} masterDeleteAccess={masterDeleteAccess} />
      )}

      {showCreateModal && (
        <BombiixModal
          show={showCreateModal}
          title={t('ASSET_LIBRARY.ADD_FILTER_TYPE')}
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateFilterType}
          saveLabel={t('ASSET_LIBRARY.SAVE')}
          saveDisabled={isCreating}
          size="md"
        >
          <label className="select-label">{t('ASSET_LIBRARY.FILTER_TITLE')}</label>
          <NewInput
            value={newFilterTypeLabel}
            maxLength={MAX_FILTER_TYPE_LABEL_LENGTH}
            placeholder={t('ASSET_LIBRARY.ENTER_FILTER_TYPE')}
            onChange={(e) => {
              setNewFilterTypeLabel(e.target.value);
              setCreateError(undefined);
            }}
            invalid={createError !== undefined}
            aria-invalid={createError !== undefined}
            data-testid="new-filter-type-input"
          />
          {createError ? <label className="text-danger m-0 p-1">{createError}</label> : null}
        </BombiixModal>
      )}
    </>
  );
}

export default AssetLibraryTab;

const MAX_FILTER_TYPES = 8;

const MAX_FILTER_TYPE_LABEL_LENGTH = 30;
